import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ClipboardCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { translateChecklistLabel } from "@/lib/checklistLabels";

export default function ChecklistTemplates() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", items: [""] });

  const load = async () => setItems((await api.get("/checklist-templates")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/checklist-templates", {
        name: form.name, items: form.items.filter((x) => x.trim()),
      });
      setOpen(false);
      setForm({ name: "", items: [""] });
      load();
      toast.success("OK");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/checklist-templates/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("templates.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("templates.title")}</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-500 hover:bg-blue-600" data-testid="new-template-btn"><Plus className="h-4 w-4 mr-1" /> {t("templates.new_template")}</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-lg">
            <DialogHeader><DialogTitle>{t("templates.new_template")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">{t("common.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="template-name" /></div>
              <div className="space-y-2">
                <Label className="text-xs">{t("templates.items")}</Label>
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input value={it} onChange={(e) =>
                      setForm({ ...form, items: form.items.map((x, i) => i === idx ? e.target.value : x) })}
                      className="bg-[#171B24] border-[#2E3241]" />
                    <Button size="icon" variant="ghost" onClick={() =>
                      setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })}
                      className="text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button variant="ghost" onClick={() => setForm({ ...form, items: [...form.items, ""] })} className="text-blue-400">
                  <Plus className="h-4 w-4 mr-1" /> {t("common.new")}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
              <Button onClick={submit} className="bg-blue-500 hover:bg-blue-600" data-testid="create-template-submit">{t("common.create")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="text-zinc-500 col-span-full">{t("common.empty")}</div>}
        {items.map((tpl) => (
          <div key={tpl.id} className="surface rounded-xl p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-blue-400" />
                <div className="font-display font-semibold">{translateChecklistLabel(tpl.name, t)}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => del(tpl.id)} className="text-zinc-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></Button>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-zinc-300 list-disc list-inside">
              {tpl.items.map((it, i) => <li key={i}>{translateChecklistLabel(it, t)}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
